import React, { useEffect, useState } from "react";
import { Truck, User, CalendarClock, Building } from "lucide-react";
import useApi from "../../hooks/useApi";

const LogisticsActivityTimeline = ({
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  referenceId, // DO / Shipment / Task id
  activityType, // e.g. DELIVERY
}) => {
  const { request, loading } = useApi();
  const [logs, setLogs] = useState([]);

  const fetchLogs = async () => {
    const url = `${BASE_URL}/api/CRMTaskHistoryList/${COMPANY_CODE}/${activityType}/${DEPARTMENT}/${referenceId}`;
    // https://cubixweberp.com:213/api/CRMTaskHistoryList/SUNRISEWEB/DOLOGITIMELINE/MAIN/30002
    try {
      const response = await request({ url, method: "GET" });
      setLogs(response || []);
    } catch (err) {
      console.error("Failed to fetch logistics logs:", err);
      setLogs([]);
    }
  };

  useEffect(() => {
    if (referenceId) fetchLogs();
  }, [referenceId]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "created":
        return "bg-green-100 text-green-700";
      case "assigned":
        return "bg-blue-100 text-blue-700";
      case "completed":
        return "bg-emerald-100 text-emerald-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="col-span-12 lg:col-span-3 space-y-4 transition-all duration-300">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-2">
          <Truck className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">
            Logistics Activity Timeline
          </h3>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto scrollbar-thin space-y-4">
          {loading && (
            <div className="text-sm text-gray-500">
              Loading activity logs...
            </div>
          )}

          {!loading && logs.length === 0 && (
            <div className="text-sm text-gray-400">
              No logistics activity recorded
            </div>
          )}

          {!loading &&
            logs.map((log, index) => (
              <div
                key={index}
                className="relative pl-6 border-l-2 border-indigo-200"
              >
                {/* Dot */}
                <span className="absolute left-[-6px] top-2 w-3 h-3 bg-indigo-500 rounded-full" />

                <div className="bg-gray-50 rounded-md p-3">
                  {/* Status + Time */}
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(
                        log.status
                      )}`}
                    >
                      {log.status}
                    </span>

                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarClock size={12} />
                      {new Date(log.create_date).toLocaleString()}
                    </span>
                  </div>

                  {/* Comment */}
                  {log.Comments && (
                    <div className="text-sm text-gray-700 font-medium mb-2">
                      {log.Comments}
                    </div>
                  )}

                  {/* Footer Info */}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {log.userid}
                    </span>

                    <span className="flex items-center gap-1">
                      <Building size={12} />
                      {log.deptno}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LogisticsActivityTimeline;
